import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ProgressButton } from '@/components/ProgressButton';
import { QuoteROIDisplay } from '@/components/quote-cards/QuoteROIDisplay';
import { QuoteDiscountSavingsDisplay } from '@/components/quote-cards/QuoteDiscountSavingsDisplay';
import { QuotePriceDisplay } from '@/components/quote-cards/QuotePriceDisplay';
import { QuoteProductInfo } from '@/components/quote-cards/QuoteProductInfo';
import { UnderwritingStatusBadge } from '@/components/UnderwritingStatusBadge';
import { ChevronUp } from 'lucide-react';
import { useTraditionalLTCPolicyManagement } from '@client/hooks/useTraditionalLTCPolicyManagement';
import { useIllustrationManagement } from '@client/hooks/useIllustrationManagement';
import { useIllustrationPolling } from '@client/hooks/useIllustrationPolling';
import { getPolicyTypeFromTraditionalLTCQuote } from '@shared/getPolicyTypeFromTraditionalLTCQuote';
import { cn } from '@/lib';
import { useSelector } from 'react-redux';
import { selectClientCalculationSettings } from '@client/model/selectors';

const formatGender = (gender: string): string => {
  return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
};

const formatMaritalStatus = (maritalClass: string): string => {
  if (maritalClass === 'Married 1 Buy') {
    return 'Married, 1 Buyer';
  }
  if (maritalClass === 'Married 2 Buy') {
    return 'Married, 2 Buyers';
  }
  return maritalClass;
};

interface TraditionalLTCQuoteWithROI extends TraditionalLTCQuote {
  roi?: number;
  formattedROI?: string;
  roiValues?: {
    projectedReturnOnInvestment: number;
    projectedCompoundAnnualGrowthRate: number;
    projectedInternalRateOfReturn: number | null;
    investmentPeriodYears: number;
  };
  projectedCostCoverage?: number;
  discountSavings?: number;
  discountPolicyType?: number;
}

export type TraditionalLTCQuoteCardProps = {
  quote: TraditionalLTCQuote | TraditionalLTCQuoteWithROI;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
  isSelected?: boolean;
  fundingSourceId?: string | null;
  underwritingResult?: 'eligible' | 'ineligible' | 'unsure' | null;
  onViewUnderwritingDetails?: () => void;
  testId?: string;
};

export function TraditionalLTCQuoteCard({
  quote,
  isExpanded,
  onToggleExpand,
  onClose,
  isSelected = false,
  fundingSourceId = null,
  underwritingResult,
  onViewUnderwritingDetails,
  testId,
}: TraditionalLTCQuoteCardProps) {
  useIllustrationPolling();

  const clientCalculationSettings = useSelector(selectClientCalculationSettings);
  const annualHealthcareInflationRate =
    clientCalculationSettings?.healthCareInflationRate ?? undefined;

  const { handleAddPolicy, handleRemovePolicy } = useTraditionalLTCPolicyManagement({
    quote,
    fundingSourceId,
    onClose,
  });

  const { hasIllustrationPdf, isCreatingIllustration, handleViewPdf, handleCreateIllustration } =
    useIllustrationManagement({
      quote,
    });

  const displayPrice = quote.primaryModalPremium;
  const fundingOption = quote.premiumFrequency;
  const carrierName = quote.carrier;
  const quoteWithROI = quote as TraditionalLTCQuoteWithROI;
  const displayROI = quoteWithROI.formattedROI;
  const projectedCostCoverage = quoteWithROI.projectedCostCoverage;

  return (
    <>
      <Card
        data-testid={testId}
        className={cn(
          'border-primary/20 hover:border-primary/40 w-full cursor-pointer rounded-xl p-5 transition-all duration-200 hover:shadow-xl hover:shadow-black/5',
          {
            'border-primary': isSelected,
          },
        )}
        onClick={isSelected ? undefined : onToggleExpand}
      >
        <div className="relative">
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex flex-col gap-2">
              <QuoteProductInfo
                carrierName={carrierName}
                productName={quote.productName}
                showLogo={true}
                showRating={true}
              />
              <UnderwritingStatusBadge
                status={underwritingResult ?? null}
                onViewDetails={
                  underwritingResult && onViewUnderwritingDetails
                    ? onViewUnderwritingDetails
                    : undefined
                }
              />
            </div>

            <QuoteROIDisplay
              projectedCostCoverage={projectedCostCoverage}
              policyType={getPolicyTypeFromTraditionalLTCQuote(quote)}
              displayROI={displayROI}
            />

            <QuoteDiscountSavingsDisplay
              discountSavings={quoteWithROI.discountSavings}
              discountPolicyType={quoteWithROI.discountPolicyType}
              policyType={getPolicyTypeFromTraditionalLTCQuote(quote)}
              carrierName={carrierName}
              annualHealthcareInflationRate={annualHealthcareInflationRate}
            />

            <QuotePriceDisplay
              price={displayPrice}
              fundingOption={fundingOption}
              label="Premium"
            />

            <div className="ml-auto flex flex-col gap-2">
              {isSelected ? (
                <Button
                  type="button"
                  variant="destructive"
                  className="rounded-lg bg-red-500/10 text-red-600 hover:bg-red-500/15"
                  onClick={handleRemovePolicy}
                >
                  Remove Product
                </Button>
              ) : (
                <Button
                  type="button"
                  className="rounded-lg"
                  onClick={handleAddPolicy}
                >
                  Select Product
                </Button>
              )}
              <ProgressButton
                testId="shop-insurance-quote-download-button"
                onClick={
                  hasIllustrationPdf
                    ? handleViewPdf
                    : event => handleCreateIllustration(event, () => {})
                }
                disabled={isCreatingIllustration}
                isLoading={isCreatingIllustration}
                isCompleted={Boolean(hasIllustrationPdf)}
                defaultText="Create Quote"
                loadingText="Generating"
                completedText="View Quote"
                className="w-full"
              />
            </div>
          </div>
          {displayROI && (
            <div className="border-primary/20 mt-4 border-t pt-4">
              <p className="text-xs text-gray-700">
                * ROI includes projections of non-guaranteed network savings discounts. Actual
                savings are subject to change, and network availability may vary.
              </p>
            </div>
          )}
          <div className="border-primary/20 mt-4 border-t">
            <div
              data-testid="shop-insurance-quote-expand-button"
              className={cn(
                'flex items-center justify-between pt-5',
                !isSelected && 'cursor-pointer',
              )}
              onClick={event => {
                event.stopPropagation();
                onToggleExpand();
              }}
            >
              <span className="sm:text-md text-primary-dark select-none text-sm font-medium">
                Product Details
              </span>
              <ChevronUp
                className={cn('text-primary-dark size-5 transition-transform duration-200', {
                  'rotate-180': isExpanded,
                })}
              />
            </div>
          </div>

          {isExpanded && (
            <div className="mt-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="select-none">
                  <h4 className="text-primary-dark mb-2 text-sm font-medium">Policy Details</h4>
                  <div className="space-y-0.5">
                    <p className="text-primary-dark/90 text-sm">
                      Age: <span data-testid="quote-detail-age">{quote.primaryAge}</span>
                    </p>
                    <p className="text-primary-dark/90 text-sm">
                      Gender:{' '}
                      <span data-testid="quote-detail-gender">
                        {formatGender(quote.primaryGender)}
                      </span>
                    </p>
                    <p className="text-primary-dark/90 text-sm">
                      Marital Status:{' '}
                      <span data-testid="quote-detail-marital-status">
                        {formatMaritalStatus(quote.maritalClass)}
                      </span>
                    </p>
                    <p className="text-primary-dark/90 text-sm">
                      Underwriting Class:{' '}
                      <span data-testid="quote-detail-underwriting-class">
                        {quote.underwritingClass}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="select-none">
                  <h4 className="text-primary-dark mb-2 text-sm font-medium">Benefits</h4>
                  <div className="space-y-0.5">
                    <p className="text-primary-dark/90 text-sm">
                      Benefit Pool:{' '}
                      <span data-testid="quote-detail-benefit-pool">
                        ${quote.benefitPool.toLocaleString()}
                      </span>
                    </p>
                    <p className="text-primary-dark/90 text-sm">
                      Daily Benefit:{' '}
                      <span data-testid="quote-detail-daily-benefit">
                        ${quote.dailyBenefitAmount}
                      </span>
                    </p>
                    <p className="text-primary-dark/90 text-sm">
                      Elimination Period:{' '}
                      <span data-testid="quote-detail-elimination-period">
                        {quote.eliminationPeriod} days
                      </span>
                    </p>
                    <p className="text-primary-dark/90 text-sm">
                      Benefit Inflation Option:{' '}
                      {quote.bioPercent === 'No BIO'
                        ? 'None'
                        : quote.bioPercent.replace(' BIO', '')}
                    </p>
                  </div>
                </div>

                <div className="select-none">
                  <h4 className="text-primary-dark mb-2 text-sm font-medium">Premiums</h4>
                  <div className="space-y-0.5">
                    <p className="text-primary-dark/90 text-sm">
                      Payment Frequency: {quote.premiumFrequency}
                    </p>
                    <p className="text-primary-dark/90 text-sm">
                      Modal Premium:{' '}
                      <span data-testid="quote-detail-premium">
                        ${quote.primaryModalPremium.toLocaleString()}
                      </span>
                    </p>
                    <p className="text-primary-dark/90 text-sm">
                      Yearly Premium: ${quote.primaryTotalYearlyPremium.toLocaleString()}
                    </p>
                    {quote.dp1Rider && <p className="text-primary-dark/90 text-sm">✓ DP1 Rider</p>}
                    {quote.nfoRider && <p className="text-primary-dark/90 text-sm">✓ NFO Rider</p>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </>
  );
}
